import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight, Package, ScrollText, ListChecks } from "lucide-react";
import customerApi from "./customerApi";

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const PARCH_EDGE = "#EFE4CC";
const GOLD = "#C9A268";

const STAGE_LABEL = {
  awaiting_payment: "Awaiting payment",
  awaiting_inventory: "Awaiting inventory",
  assigned: "Packing",
  finalised: "Packed",
  shipping: "Shipping",
  delivery: "Out for local delivery",
  delivered: "Delivered",
  quality_control: "Delivered",
  completed: "Delivered",
  complaint: "Concern raised",
};

export default function HQOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockedMsg, setLockedMsg] = useState("");

  useEffect(() => {
    customerApi.get("/customer/orders")
      .then((r) => {
        const data = r.data || {};
        // Flatten console + online + manual into one list
        const all = []
          .concat((data.console || []).map((o) => ({ ...o, source: "console" })))
          .concat((data.online  || []).map((o) => ({ ...o, source: "online" })))
          .concat((data.manual  || []).map((o) => ({ ...o, source: "manual" })))
          .concat((data.historical || []).map((o) => ({
            ...o,
            source: "heritage archive",
            stage: "completed",
            created_at: o.completed_on || o.created_at,
            historical: true,
          })));
        all.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        setOrders(all);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setLockedMsg(e.response?.data?.detail || "Please verify your email to view your order history.");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="hq-orders" className="space-y-5" style={{ color: AUBERGINE }}>
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
          <h1 className="text-4xl mt-1.5">My Orders</h1>
          <p className="italic opacity-75 text-sm mt-1">Every order linked to your email, newest first.</p>
        </div>
        <Link
          to="/account/my-picks"
          data-testid="hq-orders-pick-tubers"
          className="shrink-0 px-4 py-2.5 rounded text-xs tracking-[0.2em] uppercase font-semibold text-white inline-flex items-center gap-2 hover:opacity-90"
          style={{ backgroundColor: "#6E1E3A" }}
        >
          <ListChecks className="w-4 h-4" /> Choose my tubers
        </Link>
      </header>

      {loading ? (
        <div className="inline-flex items-center gap-2 opacity-70 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading orders…</div>
      ) : lockedMsg ? (
        <div data-testid="hq-orders-locked" className="bg-white border-2 border-dashed rounded p-10 text-center opacity-80" style={{ borderColor: "#C49247" }}>
          <ScrollText className="w-8 h-8 mx-auto mb-3 opacity-50" />
          {lockedMsg}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-dashed rounded p-10 text-center opacity-70" style={{ borderColor: PARCH_EDGE }}>
          <ScrollText className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">No orders yet.</div>
          <div className="text-xs italic mt-1">Once Kayla logs your first order it&apos;ll appear here.</div>
          <a href="/account/contact" className="inline-block mt-4 text-[10px] tracking-[0.25em] uppercase font-semibold underline" style={{ color: PLUM }}>
            Contact Heritage →
          </a>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="hq-orders-list">
          {orders.map((o) => (
            <li key={`${o.source}-${o.id || o.order_number}`}>
              {o.historical ? (
                <div
                  data-testid={`hq-order-historical-${o.id}`}
                  className="block bg-white/70 border border-dashed rounded-md p-4"
                  style={{ borderColor: PARCH_EDGE }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>
                        <ScrollText className="w-3 h-3 inline mr-1" />
                        Historical Completed Order{String(o.completed_on || o.created_at || "").slice(0, 4) ? ` (${String(o.completed_on || o.created_at || "").slice(0, 4)})` : ""}
                      </div>
                      <div className="text-base mt-0.5 truncate">
                        {o.items ? o.items.split("\n")[0] : "Past order"}
                      </div>
                      <div className="text-xs opacity-70 mt-0.5">{String(o.completed_on || o.created_at || "").slice(0, 10)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: GOLD }}>
                        Completed
                      </div>
                      <div className="text-sm mt-0.5">NZ${Number(o.total || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ) : (
              <Link
                to={`/account/orders/${encodeURIComponent(o.id || o.order_number)}`}
                data-testid={`hq-order-${o.id || o.order_number}`}
                className="block bg-white border rounded-md p-4 hover:shadow-md transition-shadow"
                style={{ borderColor: PARCH_EDGE }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>
                      <Package className="w-3 h-3 inline mr-1" />{o.source}
                    </div>
                    <div className="text-base mt-0.5 truncate">
                      {o.order_number ? `Order #${o.order_number}` : `Ref ${String(o.id || "").slice(0, 8)}`}
                    </div>
                    <div className="text-xs opacity-70 mt-0.5">{String(o.created_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: GOLD }}>
                      {STAGE_LABEL[o.stage] || o.stage || "-"}
                    </div>
                    <div className="text-sm mt-0.5">NZ${Number(o.total || 0).toFixed(2)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </div>
              </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
